import { Formik } from 'formik';
import { capitalize } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';

import AutocompleteField from '@components/Form/AutocompleteField';
import DropdownField from '@components/Form/DropdownField';
import TextField from '@components/Form/TextField';
import { useEntitiesCreate, useProfile, useProfileUpdate } from '@hooks';
import { BLANK_OPTION, BLANK_RECORD_OPTION } from '@src/constants';
import { autocompleteEntities } from '@utils/backend-api/entities';
import {
  COMPANY_SIZE_OPTIONS,
  ENTITY_TYPE_JOB_TITLE,
  ENTITY_TYPE_ORGANIZATION,
  JOB_FUNCTION_OPTIONS,
  JOB_LEVEL_OPTIONS,
  ORGANIZATION_INDUSTRY_OPTIONS,
  PROFILE_FIELD_OTHER_VALUE,
  PROFILE_OTHER_OPTION,
} from '@utils/backend-api/profileSettings';
import { AutocompleteOption, AutocompleteRecord } from '@utils/interfaces';

import IndustryFields from './ProfileFields/IndustryFields';
import JobFunctionFields from './ProfileFields/JobFunctionFields';
import JobLevelFields from './ProfileFields/JobLevelFields';
import SubIndustryFields from './ProfileFields/SubIndustryFields';
import SubIndustryList from './ProfileFields/SubIndustryList';
import SubJobFunctionFields from './ProfileFields/SubJobFunctionFields';
import SubJobFunctionList from './ProfileFields/SubJobFunctionList';
import StepContainer from './StepContainer';
import schema from './schema';

interface StepProfileProps {
  currentUserId: number;
  nextStep: () => void;
}

export interface StepProfileFormProps {
  company: AutocompleteRecord;
  companySize: AutocompleteOption;
  customIndustry: AutocompleteRecord;
  customJobFunction: AutocompleteRecord;
  customJobLevel: AutocompleteRecord;
  customSubIndustry: AutocompleteRecord;
  customSubJobFunction: AutocompleteRecord;
  firstName: string;
  industry: AutocompleteOption;
  jobFunction: AutocompleteOption;
  jobLevel: AutocompleteOption;
  jobTitle?: AutocompleteRecord;
  lastName: string;
  postalCode: string;
  subIndustry: AutocompleteOption;
  subJobFunction: AutocompleteOption;
}

const INITIAL_STATE = {
  company: BLANK_RECORD_OPTION,
  companySize: BLANK_OPTION,
  customIndustry: BLANK_RECORD_OPTION,
  customJobFunction: BLANK_RECORD_OPTION,
  customJobLevel: BLANK_RECORD_OPTION,
  customSubIndustry: BLANK_RECORD_OPTION,
  customSubJobFunction: BLANK_RECORD_OPTION,
  firstName: '',
  industry: BLANK_OPTION,
  jobFunction: BLANK_OPTION,
  jobLevel: BLANK_OPTION,
  jobTitle: BLANK_RECORD_OPTION,
  lastName: '',
  postalCode: '',
  subIndustry: BLANK_OPTION,
  subJobFunction: BLANK_OPTION,
};

const StepProfile = ({ nextStep, currentUserId }: StepProfileProps) => {
  const [initialValues, setInitialValues] = useState(INITIAL_STATE);
  const { mutateAsync: createEntityAsync } = useEntitiesCreate();
  const { saveProfile, error } = useProfileUpdate(currentUserId);
  const { profile, isLoadingProfile } = useProfile();

  const handleSubmit = async (values) => {
    const sanitizedParams = {
      name: `${values.firstName} ${values.lastName}` || '',
      company_name: values.company?.label || '',
      title_at_company: values.jobTitle?.label || '',
      job_level:
        values.jobLevel.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customJobLevel.label
          : values.jobLevel.value,
      job_function:
        values.jobFunction.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customJobFunction.label
          : values.jobFunction.value,
      sub_job_function:
        values.subJobFunction.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customSubJobFunction.label
          : values.subJobFunction.value,
      organization_industry:
        values.industry.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customIndustry.label
          : values.industry.value,
      organization_sub_industry:
        values.subIndustry.value === PROFILE_FIELD_OTHER_VALUE
          ? values.customSubIndustry.label
          : values.subIndustry.value,

      company_size: values.companySize.value,
      postal_code: values.postalCode,
      profile_enabled: true,
    };
    saveProfile(sanitizedParams);
    nextStep();
  };

  useEffect(() => {
    const getSelectedOptions = (possibleOptions, profileKey) => {
      const selectedStandardValue = profile?.[profileKey];
      const standardOption =
        possibleOptions?.find((item) => item.value === selectedStandardValue) || BLANK_OPTION;
      const customOption = BLANK_RECORD_OPTION;

      if (selectedStandardValue && !standardOption.label) {
        return {
          standardOption: PROFILE_OTHER_OPTION,
          customOption: { label: capitalize(selectedStandardValue), value: selectedStandardValue },
        };
      }

      return { standardOption, customOption };
    };

    if (!isLoadingProfile) {
      const name = profile?.name?.split(' ');
      const companySize = COMPANY_SIZE_OPTIONS.find(
        (item) => item.value === profile?.companySize
      ) || { ...BLANK_OPTION };

      const industryValueAndCustomValue = getSelectedOptions(
        ORGANIZATION_INDUSTRY_OPTIONS,
        'organizationIndustry'
      );
      const jobFunctionValueAndCustomValue = getSelectedOptions(
        JOB_FUNCTION_OPTIONS,
        'jobFunction'
      );
      const jobLevelValueAndCustomValue = getSelectedOptions(JOB_LEVEL_OPTIONS, 'jobLevel');
      const jobFunction = JOB_FUNCTION_OPTIONS.find((item) => item.value === profile?.jobFunction);
      const industry = ORGANIZATION_INDUSTRY_OPTIONS.find(
        (item) => item.value === profile?.organizationIndustry
      );
      const subJobFunctionValueAndCustomValue = getSelectedOptions(
        SubJobFunctionList[jobFunction?.label],
        'subJobFunction'
      );
      const subIndustryValueAndCustomValue = getSelectedOptions(
        SubIndustryList[industry?.label],
        'organizationSubIndustry'
      );

      setInitialValues({
        company: profile.company
          ? { label: capitalize(profile.company.label), value: profile.company.value }
          : BLANK_RECORD_OPTION,
        companySize: companySize,
        customIndustry: industryValueAndCustomValue.customOption,
        customJobFunction: jobFunctionValueAndCustomValue.customOption,
        customJobLevel: jobLevelValueAndCustomValue.customOption,
        customSubIndustry: subIndustryValueAndCustomValue.customOption,
        customSubJobFunction: subJobFunctionValueAndCustomValue.customOption,
        firstName: name[0] === 'Guest' ? '' : name[0] || '',
        industry: industryValueAndCustomValue.standardOption,
        jobFunction: jobFunctionValueAndCustomValue.standardOption,
        jobLevel: jobLevelValueAndCustomValue.standardOption,
        jobTitle: profile.titleAtCompany || { label: '', value: 0 },
        lastName: name[1] || '',
        postalCode: profile.postalCode || '',
        subIndustry: subIndustryValueAndCustomValue.standardOption,
        subJobFunction: subJobFunctionValueAndCustomValue.standardOption,
      });
    }
  }, [
    isLoadingProfile,
    profile,
    profile?.jobFunction,
    profile?.jobLevel,
    profile?.subJobFunction,
    profile?.organizationIndustry,
    profile?.organizationSubIndustry,
    profile?.organizationSize,
    profile?.postalCode,
  ]);

  if (isLoadingProfile) return null;
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {(formik) => (
        <StepContainer
          heading="Create Your Public Subscriber Profile"
          subheading="Join our exclusive community by adding your profile to the subscriber directory."
          onContinue={formik.handleSubmit}
          disableContinue={formik.isSubmitting}
          continueButtonType="submit"
          hideSkip
          nextStep={nextStep}
          error={error}
        >
          <Form className="form-container">
            <div className="mb-8 flex gap-4">
              <TextField name="firstName" label="First Name&#42;" className="w-1/2" />
              <TextField name="lastName" label="Last Name&#42;" className="w-1/2" />
            </div>
            <div className="mt-24">
              <AutocompleteField
                label="Job Title"
                name="jobTitle"
                fetchMethod={(value) =>
                  autocompleteEntities({
                    fragment: value,
                    entity_type: ENTITY_TYPE_JOB_TITLE,
                  })
                }
                createMethod={(value) =>
                  createEntityAsync({
                    name: value,
                    entity_type: ENTITY_TYPE_JOB_TITLE,
                    word_type: 'proper',
                  })
                }
                placeholder="Type to search titles…"
              />
            </div>
            <JobLevelFields />
            <JobFunctionFields />
            <SubJobFunctionFields />
            <div className="mt-24">
              <AutocompleteField
                label="Organization&#42;"
                name="company"
                fetchMethod={(value) =>
                  autocompleteEntities({
                    fragment: value,
                    entity_type: ENTITY_TYPE_ORGANIZATION,
                  })
                }
                createMethod={(value) =>
                  createEntityAsync({
                    name: value,
                    entity_type: ENTITY_TYPE_ORGANIZATION,
                    word_type: 'proper',
                  })
                }
                placeholder="Type to search organizations…"
              />
            </div>

            <IndustryFields />
            <SubIndustryFields />
            <DropdownField
              name="companySize"
              options={COMPANY_SIZE_OPTIONS}
              label="Company Size"
              placeholder="Select"
              isClearable={false}
              scrollIntoView
            />
            <div className="mt-24">
              <TextField name="postalCode" label="Your Postal Code" />
            </div>
          </Form>
        </StepContainer>
      )}
    </Formik>
  );
};

export default StepProfile;
